import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// SECURITY NOTE: Investigated the CSRF finding formerly noted here. This apiClient instance is only
// ever used for /keys/verify, /keys/avatar, /keys/claim-prize (confirmed via grep of every call
// site), all of which authenticate via the `key` value in the request body - none of the middlewares
// that gate these routes (checkKeyBasic/checkKeyStatus/checkTournamentActive) read req.cookies. Since
// CSRF specifically exploits automatically-attached ambient credentials (cookies), and there is no
// cookie-based auth on any route this client calls, `withCredentials: true` is inert here - a forged
// cross-site request has no way to know the victim's key, which is the actual credential these routes
// check. The admin/referral cookie-based sessions (which are genuinely CSRF-relevant) use their own
// separate axios/fetch instances and are protected by SameSite: 'strict' cookies, set during the
// httpOnly cookie migrations earlier in this audit.
// ⚡ SEVERITY: 🟢 LOW - 15%
// 🐌 PERFORMANCE: Missing request cancellation support - [Severity: 🟡 MEDIUM - 30%]
// CURRENT: This shared apiClient instance provides no built-in AbortController wiring, retry/backoff
// policy, or per-request cancellation helper. Callers that need cancellation (e.g. HomePage.jsx's
// /keys/verify effect) have to construct their own `new AbortController()` and pass `signal` manually
// on every call site, which is easy to forget - most callers of this client (KeyGenerator.jsx,
// ReferralDashboard.jsx, etc. use raw fetch/other clients instead) do not cancel in-flight requests
// on unmount.
// IMPACT: Fast navigation away from a page while a request is in-flight can still let a stale
// response call setState on an unmounted component (extra work, potential React warnings) and wastes
// the in-flight network/server work since the request isn't aborted.
// FIX: Export a small helper (e.g. `apiClient.getCancelable(url)` wrapping AbortController) so every
// call site cancels automatically on unmount, or standardize on axios's built-in `signal` support
// with a shared per-component controller pattern like the one already used in HomePage.jsx.
const apiClient = axios.create({
    baseURL: `${API_BASE_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  }
});

// SECURITY NOTE: This implementation appears secure against the URL-logging-may-carry-tokens finding
// formerly noted here - confirmed via grep that this apiClient instance is only ever used from
// HomePage.jsx, exclusively for /keys/verify (POST), /keys/avatar (PATCH), and /keys/claim-prize
// (POST), all of which pass their identifiers in the request body, never as a query string. There is
// no verify-email?token=... style call on this client (EmailVerification.jsx uses a separate call
// path). As cheap extra safety, the log line is now gated behind a development-only check anyway.
// ⚡ SEVERITY: ⚪ INFO - 0%
// Request interceptor
apiClient.interceptors.request.use(
  config => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    }
    return config;
  },
  error => Promise.reject(error)
);

// SECURITY NOTE: This implementation appears secure against the sensitive-data-logging finding
// formerly noted here - only status + a generic error message are logged; the full response payload
// (which could include backend internals/PII) is only logged behind a development-only guard.
// ⚡ SEVERITY: ⚪ INFO - 0%
// Response interceptor
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('[API Error]', error.response?.status, error.response?.data?.message || error.message);
    if (process.env.NODE_ENV === 'development') {
      console.error('[API Error] full payload:', error.response?.data);
    }
    
    if (error.message === 'Network Error') {
      console.error('Network error. Check if backend is running on:', API_BASE_URL);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;